import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../api';

const blankUser = {
  name: '',
  email: '',
  password: '',
  role: 'staff',
  permissions: {
    manageInquiries: true,
    manageBlog: true,
    manageAlerts: true,
    manageDestinations: false,
    manageUsers: false,
    viewStatistics: true
  }
};

const allPermissionsTrue = {
  manageInquiries: true,
  manageBlog: true,
  manageAlerts: true,
  manageDestinations: true,
  manageUsers: true,
  viewStatistics: true
};

const permissionKeys = [
  ['manageInquiries',   'Manage Inquiries'],
  ['manageBlog',        'Manage Blog'],
  ['manageAlerts',      'Manage Alerts'],
  ['manageDestinations','Manage Destinations'],
  ['manageUsers',       'Manage Admin Users'],
  ['viewStatistics',    'View Statistics']
];

export default function AdminUsers() {
  const [users, setUsers]     = useState([]);
  const [form, setForm]       = useState(blankUser);
  const [error, setError]     = useState('');
  const [showPass, setShowPass] = useState(false);

  const load = function() {
    api.get('/auth/users').then(function(res) { setUsers(res.data); }).catch(function() {});
  };
  useEffect(function() { load(); }, []);

  const activeCount = users.filter(function(u) { return u.isActive; }).length;

  function handleRoleChange(e) {
    var role = e.target.value;
    setForm(function(prev) {
      return {
        ...prev,
        role: role,
        // Auto-check all permissions when superadmin is selected
        permissions: role === 'superadmin' ? allPermissionsTrue : prev.permissions
      };
    });
  }

  function togglePermission(key) {
    // Don't allow toggling individual permissions for superadmin
    if (form.role === 'superadmin') return;
    setForm(function(prev) {
      return {
        ...prev,
        permissions: { ...prev.permissions, [key]: !prev.permissions[key] }
      };
    });
  }

  const handleSubmit = async function(e) {
    e.preventDefault();
    setError('');
    try {
      await api.post('/auth/users', form);
      setForm(blankUser);
      setShowPass(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create user');
    }
  };

  const deactivate = async function(id) {
    if (!window.confirm('Deactivate this admin user?')) return;
    await api.delete('/auth/users/' + id);
    load();
  };

  const reactivate = async function(id) {
    await api.put('/auth/users/' + id, { isActive: true });
    load();
  };

  const deleteUser = async function(id, name) {
    if (!window.confirm('Permanently delete ' + name + '? This cannot be undone.')) return;
    await api.delete('/auth/users/' + id);
    load();
  };

  return (
    <AdminLayout>
      <h1 style={{ marginBottom: 8 }}>Admin Users</h1>
      <p style={{ opacity: 0.65, marginBottom: 24 }}>
        Up to 10 active admin users. Currently active: <strong>{activeCount}/10</strong>.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 28 }}>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="card" style={{ padding: 24 }}>
          <h3 style={{ marginBottom: 18 }}>Add Admin User</h3>

          <div className="form-field" style={{ marginBottom: 14 }}>
            <label>Name</label>
            <input required value={form.name} onChange={function(e) { setForm({ ...form, name: e.target.value }); }} placeholder="Full name" />
          </div>

          <div className="form-field" style={{ marginBottom: 14 }}>
            <label>Email</label>
            <input type="email" required value={form.email} onChange={function(e) { setForm({ ...form, email: e.target.value }); }} placeholder="email@example.com" />
          </div>

          {/* Password with show/hide */}
          <div className="form-field" style={{ marginBottom: 14 }}>
            <label>Temporary Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                required
                minLength={6}
                placeholder="Min. 6 characters"
                value={form.password}
                onChange={function(e) { setForm({ ...form, password: e.target.value }); }}
                style={{ paddingRight: 56, width: '100%' }}
              />
              <button
                type="button"
                onClick={function() { setShowPass(!showPass); }}
                style={{
                  position: 'absolute', right: 10, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none',
                  cursor: 'pointer', fontFamily: 'var(--font-body)',
                  fontWeight: 700, fontSize: '0.75rem',
                  color: 'var(--bj-espresso)', opacity: 0.45,
                  padding: '2px 4px', userSelect: 'none',
                  transition: 'opacity 0.15s'
                }}
                onMouseEnter={function(e) { e.currentTarget.style.opacity = 0.85; }}
                onMouseLeave={function(e) { e.currentTarget.style.opacity = 0.45; }}
              >
                {showPass ? 'HIDE' : 'SHOW'}
              </button>
            </div>
          </div>

          {/* Role */}
          <div className="form-field" style={{ marginBottom: 14 }}>
            <label>Role</label>
            <select value={form.role} onChange={handleRoleChange}>
              <option value="staff">Staff</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>

          {/* Permissions */}
          <div className="form-field" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ margin: 0 }}>Permissions</label>
              {form.role === 'superadmin' && (
                <span style={{
                  fontSize: '0.72rem', fontWeight: 700,
                  color: 'var(--bj-clay)',
                  background: 'rgba(63,114,175,0.1)',
                  padding: '2px 8px', borderRadius: 20
                }}>
                  All granted (superadmin)
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {permissionKeys.map(function(item) {
                var key   = item[0];
                var label = item[1];
                var isChecked = !!form.permissions[key];
                var isSuperAdmin = form.role === 'superadmin';
                return (
                  <label
                    key={key}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '18px 1fr',
                      alignItems: 'center',
                      gap: 10,
                      fontWeight: 400,
                      fontSize: '0.9rem',
                      cursor: isSuperAdmin ? 'not-allowed' : 'pointer',
                      padding: '7px 10px',
                      borderRadius: 6,
                      background: isChecked ? 'rgba(63,114,175,0.08)' : 'var(--bj-cream-soft)',
                      border: '1px solid ' + (isChecked ? 'rgba(63,114,175,0.25)' : 'var(--bj-sand)'),
                      opacity: isSuperAdmin ? 0.7 : 1,
                      transition: 'all 0.15s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={function() { togglePermission(key); }}
                      disabled={isSuperAdmin}
                      style={{
                        width: 16, height: 16,
                        cursor: isSuperAdmin ? 'not-allowed' : 'pointer',
                        margin: 0, accentColor: 'var(--bj-clay)'
                      }}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {error && <p className="form-message error" style={{ marginBottom: 12 }}>{error}</p>}

          <button
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center' }}
            disabled={activeCount >= 10}
          >
            {activeCount >= 10 ? 'Maximum users reached' : 'Create User'}
          </button>
        </form>

        {/* USER LIST */}
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ marginBottom: 16 }}>All Admin Users</h3>
          {users.length === 0 && <p style={{ opacity: 0.6 }}>No users yet.</p>}
          {users.map(function(u) {
            return (
              <div key={u._id} style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '12px 0',
                borderBottom: '1px solid var(--bj-sand)', gap: 10
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '0.92rem' }}>{u.name}</strong>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      padding: '2px 8px', borderRadius: 20,
                      background: u.role === 'superadmin' ? 'rgba(63,114,175,0.12)' : 'var(--bj-sand)',
                      color: u.role === 'superadmin' ? 'var(--bj-clay)' : 'var(--bj-espresso)'
                    }}>
                      {u.role}
                    </span>
                    {!u.isActive && (
                      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#ef4444' }}>
                        Inactive
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '3px 0 0', fontSize: '0.8rem', opacity: 0.55 }}>{u.email}</p>
                </div>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {u.isActive ? (
                    <button
                      className="btn btn-outline"
                      style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                      onClick={function() { deactivate(u._id); }}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      className="btn btn-outline"
                      style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                      onClick={function() { reactivate(u._id); }}
                    >
                      Reactivate
                    </button>
                  )}
                  <button
                    className="btn btn-outline"
                    style={{ padding: '4px 10px', fontSize: '0.8rem', borderColor: '#ef4444', color: '#ef4444' }}
                    onClick={function() { deleteUser(u._id, u.name); }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}